import type {
  AnalyticsWidgetResult,
  GradeWiseParticipationItem,
  SchoolReportData,
  SchoolReportGradeBlock,
  SchoolReportMetadata,
  SchoolReportParticipationDetails,
  SchoolReportStudentRow,
  SchoolReportSubjectStats,
  SchoolReportSummaryItem,
  SchoolReportWidgetBundles,
} from '@/types/schoolReport';

function asNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const cleaned = value.trim().replace(/%$/, '').replace(/,/g, '');
    if (cleaned !== '' && !Number.isNaN(Number(cleaned))) return Number(cleaned);
  }
  return fallback;
}

function asString(value: unknown, fallback = ''): string {
  if (value == null) return fallback;
  return String(value);
}

function asOptionalString(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  return String(value);
}

function normalizeGrade(value: unknown): string {
  const raw = asString(value).trim();
  if (!raw) return 'Grade-1';
  if (/^grade[-\s]?\d+/i.test(raw)) {
    const num = raw.match(/\d+/)?.[0] ?? '1';
    return `Grade-${num}`;
  }
  if (/^\d+$/.test(raw)) return `Grade-${raw}`;
  return raw;
}

function gradeSortValue(grade: string): number {
  const n = Number(grade.match(/\d+/)?.[0]);
  return Number.isFinite(n) ? n : 999;
}

function formatPercent(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  const n = asNumber(value, Number.NaN);
  if (Number.isNaN(n)) return asString(value);
  return `${Number(n.toFixed(1))}%`;
}

/** TOTAL_TIME from API looks like fractional hours → "Xm Ys". */
function formatTotalTime(value: unknown): string {
  const hours = asNumber(value, Number.NaN);
  if (Number.isNaN(hours) || hours <= 0) return '—';
  const totalSeconds = Math.round(hours * 3600);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  if (m <= 0) return `${s}s`;
  return `${m}m ${s}s`;
}

/**
 * BEST_CHAPTERS / IMPROVEMENT_CHAPTERS arrive as JSON strings of nested arrays:
 * [["Motion in a Plane"], ["Laws of Motion"]]
 */
function parseChapterLists(value: unknown): string[] {
  if (Array.isArray(value)) {
    return flattenChapterNodes(value);
  }
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    if (Array.isArray(parsed)) return flattenChapterNodes(parsed);
  } catch {
    return value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

function flattenChapterNodes(nodes: unknown[]): string[] {
  const out: string[] = [];
  for (const node of nodes) {
    if (typeof node === 'string' && node.trim()) out.push(node.trim());
    else if (Array.isArray(node)) out.push(...flattenChapterNodes(node));
  }
  return [...new Set(out)];
}

function buildParticipation(
  gradewiseRows: Record<string, unknown>[]
): SchoolReportParticipationDetails {
  const gradewise_participation: GradeWiseParticipationItem[] = gradewiseRows
    .map((row) => {
      const participated = asNumber(row.GRADEWISE_PARTICIPATION);
      const registered = Math.max(asNumber(row.GRADEWISE_REGISTRATIONS), participated);
      return {
        grade: normalizeGrade(row.GRADE_NAME ?? row.GRADE),
        participated,
        registered,
      };
    })
    .sort((a, b) => gradeSortValue(a.grade) - gradeSortValue(b.grade));

  const sample = gradewiseRows[0] ?? {};
  const overallParticipated = asNumber(sample.OVERALL_PARTICIPATIONS, 
    gradewise_participation.reduce((s, g) => s + g.participated, 0)
  );
  const overallRegistered = asNumber(
    sample.OVERALL_REGISTRATIONS,
    gradewise_participation.reduce((s, g) => s + g.registered, 0)
  );
  const overallPercent = asNumber(
    sample.OVERALL_PARTICIPATION_PERCENT,
    overallRegistered > 0
      ? Math.round((overallParticipated / overallRegistered) * 1000) / 10
      : 0
  );
  const otherSchoolsAvg = asNumber(sample.OTHER_SCHOOLS_PERCENT, overallPercent);

  return {
    total_participation: `${overallParticipated}/${overallRegistered}`,
    overall_participation_percent: overallPercent,
    other_schools_avg_percent: otherSchoolsAvg,
    gradewise_participation,
  };
}

function buildSubjectFromSectionRow(row: Record<string, unknown>): {
  name: string;
  stats: SchoolReportSubjectStats;
} {
  const name = asString(row.SUBJECT, 'Subject');
  const accuracy = formatPercent(row.SUB_ACCURACY);
  const otherAvg = formatPercent(row.SUB_OTHER_SCHOOL_AVG);

  return {
    name,
    stats: {
      max_marks: asNumber(row.SUB_MAX_MARKS),
      avg_marks: asNumber(row.SUB_AVG_MARKS),
      total_marks: asNumber(row.SUB_TOTAL_MARKS),
      accuracy,
      other_school_avg: otherAvg,
      best_chapters: parseChapterLists(row.BEST_CHAPTERS),
      improvement_chapters: parseChapterLists(row.IMPROVEMENT_CHAPTERS),
    },
  };
}

function isAttemptedStatus(status: unknown): boolean {
  const s = asString(status).trim().toLowerCase();
  if (!s) return false;
  return s === 'attempted' || s === 'submitted' || s === 'completed';
}

function buildStudentFromRow(row: Record<string, unknown>): SchoolReportStudentRow {
  const current = asNumber(row.CURRENT_MARKS);
  const total = asNumber(row.TOTAL_MARKS);
  const marksObtained =
    total > 0 ? `${current}/${total}` : current > 0 ? String(current) : '—';

  return {
    rank: asString(row.RANK, '—'),
    student_name: asString(row.STUDENT_NAME, '—'),
    marks_obtained: marksObtained,
    accuracy: formatPercent(row.ACCURACY) ?? '—',
    total_time: formatTotalTime(row.TOTAL_TIME),
  };
}

/**
 * One UI block per (grade, exam) — matches GradeWiseDetails exam dropdown.
 */
function buildGradewiseData(bundle: SchoolReportWidgetBundles): SchoolReportGradeBlock[] {
  const { sectionwiseRows, studentwiseRows } = bundle;
  const blocks = new Map<string, SchoolReportGradeBlock>();

  const ensureBlock = (grade: string, examName?: string): SchoolReportGradeBlock => {
    const key = `${grade}::${examName ?? ''}`;
    let block = blocks.get(key);
    if (!block) {
      block = {
        grade,
        exam_name: examName || undefined,
        subjects: {},
        student_performance: {
          total_attempted: 0,
          total_not_attempted: 0,
          attempted: [],
          not_attempted: [],
        },
      };
      blocks.set(key, block);
    }
    return block;
  };

  for (const row of sectionwiseRows) {
    const grade = normalizeGrade(row.GRADE_NAME ?? row.GRADE);
    const examName = asOptionalString(row.EXAM_NAME);
    const block = ensureBlock(grade, examName);
    const { name, stats } = buildSubjectFromSectionRow(row);
    block.subjects = { ...(block.subjects ?? {}), [name]: stats };
  }

  for (const row of studentwiseRows) {
    const grade = normalizeGrade(row.GRADE_NAME ?? row.GRADE);
    const examName = asOptionalString(row.EXAM_NAME);
    // Attach students to matching exam block when present; else grade-only block
    let block = blocks.get(`${grade}::${examName ?? ''}`);
    if (!block && examName) {
      // Prefer an existing grade block for this exam created from sectionwise
      block = ensureBlock(grade, examName);
    } else if (!block) {
      block = ensureBlock(grade, examName);
    }

    const student = buildStudentFromRow(row);
    const perf = block.student_performance!;
    if (isAttemptedStatus(row.SUBMISSION_STATUS)) {
      perf.attempted = [...(perf.attempted ?? []), student];
    } else {
      perf.not_attempted = [...(perf.not_attempted ?? []), student];
    }
    perf.total_attempted = perf.attempted?.length ?? 0;
    perf.total_not_attempted = perf.not_attempted?.length ?? 0;
  }

  // If sectionwise empty but we have gradewise grades, still expose empty grade shells
  if (blocks.size === 0) {
    for (const row of bundle.gradewiseRows) {
      ensureBlock(normalizeGrade(row.GRADE_NAME ?? row.GRADE));
    }
  }

  return [...blocks.values()].sort((a, b) => {
    const g = gradeSortValue(a.grade) - gradeSortValue(b.grade);
    if (g !== 0) return g;
    return asString(a.exam_name).localeCompare(asString(b.exam_name));
  });
}

interface SubjectAccuracyEntry {
  grade: string;
  exam_name?: string;
  subject: string;
  accuracy: number;
}

function collectSubjectAccuracies(
  gradewiseData: SchoolReportGradeBlock[]
): SubjectAccuracyEntry[] {
  const entries: SubjectAccuracyEntry[] = [];
  for (const block of gradewiseData) {
    const subjects = block.subjects ?? {};
    for (const [subject, stats] of Object.entries(subjects)) {
      const accuracy = asNumber(stats.accuracy, Number.NaN);
      if (Number.isNaN(accuracy)) continue;
      entries.push({
        grade: block.grade,
        exam_name: block.exam_name,
        subject,
        accuracy,
      });
    }
  }
  return entries;
}

function findAccuracyExtremes(entries: SubjectAccuracyEntry[]): {
  highest?: SubjectAccuracyEntry;
  lowest?: SubjectAccuracyEntry;
} {
  if (entries.length === 0) return {};
  let highest = entries[0];
  let lowest = entries[0];
  for (const entry of entries) {
    if (entry.accuracy > highest.accuracy) highest = entry;
    if (entry.accuracy < lowest.accuracy) lowest = entry;
  }
  return { highest, lowest };
}

function describeSubjectEntry(entry: SubjectAccuracyEntry): string {
  const examPart = entry.exam_name ? ` ${entry.exam_name}` : '';
  return `${entry.subject} of ${entry.grade}${examPart}`;
}

/* -------------------------------------------------------------------------
 * Grade-group best/worst performance summary
 *
 * Grade groups: "1-5" (Aptitude Test), "6-10" (Foundation Test),
 * "11-13-NEET", "11-13-JEE". Grades 11-13 are split by exam_name; rows in
 * that band that are neither NEET nor JEE are excluded from grouping.
 * ---------------------------------------------------------------------- */

type GradeGroupKey = '1-5' | '6-10' | '11-13-NEET' | '11-13-JEE';

const GRADE_GROUP_ORDER: GradeGroupKey[] = ['1-5', '6-10', '11-13-NEET', '11-13-JEE'];

function resolveGradeGroup(grade: string, examName?: string): GradeGroupKey | null {
  const n = gradeSortValue(grade);
  if (!Number.isFinite(n) || n === 999) return null;
  const exam = asString(examName).toLowerCase();
  if (n >= 1 && n <= 5) {
    if (exam.includes("aptitude")) return "1-5";
    return null;
  }
  if (n >= 6 && n <= 10) {
    if (exam.includes("foundation")) return "6-10";
    return null;
  }
  if (n >= 11 && n <= 13) {
    if (exam.includes("neet")) return "11-13-NEET";
    if (exam.includes("jee")) return "11-13-JEE";
    return null;
  }
  return null;
}

interface GradeGroupStats {
  key: GradeGroupKey;
  grades: Set<string>;
  studentPercentSum: number;
  studentCount: number;
  subjectPercentSums: Map<string, number>;
  subjectPercentCounts: Map<string, number>;
}

function createGradeGroupStats(key: GradeGroupKey): GradeGroupStats {
  return {
    key,
    grades: new Set<string>(),
    studentPercentSum: 0,
    studentCount: 0,
    subjectPercentSums: new Map(),
    subjectPercentCounts: new Map(),
  };
}

function getOrCreateStats(
  map: Map<GradeGroupKey, GradeGroupStats>,
  key: GradeGroupKey
): GradeGroupStats {
  let stats = map.get(key);
  if (!stats) {
    stats = createGradeGroupStats(key);
    map.set(key, stats);
  }
  return stats;
}

/**
 * Buckets studentwise rows (attempted/submitted only) and sectionwise rows
 * into grade groups, computing:
 *  - average student percentage per group (CURRENT_MARKS / TOTAL_MARKS)
 *  - average subject percentage per group (SUB_AVG_MARKS / SUB_TOTAL_MARKS)
 */
function computeGradeGroupStats(
  bundle: SchoolReportWidgetBundles
): Map<GradeGroupKey, GradeGroupStats> {
  const stats = new Map<GradeGroupKey, GradeGroupStats>();

  for (const row of bundle.studentwiseRows) {
    if (!isAttemptedStatus(row.SUBMISSION_STATUS)) continue;
    const grade = normalizeGrade(row.GRADE_NAME ?? row.GRADE);
    const examName = asOptionalString(row.EXAM_NAME);
    const groupKey = resolveGradeGroup(grade, examName);
    if (!groupKey) continue;

    const total = asNumber(row.TOTAL_MARKS);
    if (total <= 0) continue;
    const current = asNumber(row.CURRENT_MARKS);
    const percent = (current / total) * 100;

    const entry = getOrCreateStats(stats, groupKey);
    entry.grades.add(grade);
    entry.studentPercentSum += percent;
    entry.studentCount += 1;
  }

  for (const row of bundle.sectionwiseRows) {
    const grade = normalizeGrade(row.GRADE_NAME ?? row.GRADE);
    const examName = asOptionalString(row.EXAM_NAME);
    const groupKey = resolveGradeGroup(grade, examName);
    if (!groupKey) continue;

    const totalMarks = asNumber(row.SUB_TOTAL_MARKS);
    if (totalMarks <= 0) continue;
    const avgMarks = asNumber(row.SUB_AVG_MARKS);
    const percent = (avgMarks / totalMarks) * 100;
    const subject = asString(row.SUBJECT, 'Subject');

    const entry = getOrCreateStats(stats, groupKey);
    entry.grades.add(grade);
    entry.subjectPercentSums.set(subject, (entry.subjectPercentSums.get(subject) ?? 0) + percent);
    entry.subjectPercentCounts.set(subject, (entry.subjectPercentCounts.get(subject) ?? 0) + 1);
  }

  return stats;
}

interface GradeGroupSubjectSummary {
  key: GradeGroupKey;
  label: string;
  avgPercent: number;
  topSubject?: string;
  topSubjectPercent?: number;
}

function gradeGroupLabel(key: GradeGroupKey, grades: Set<string>): string {
  const nums = [...grades].map(gradeSortValue).filter((n) => Number.isFinite(n) && n !== 999);
  const min = nums.length ? Math.min(...nums) : undefined;
  const max = nums.length ? Math.max(...nums) : undefined;
  const range = min != null && max != null ? (min === max ? `${min}` : `${min}-${max}`) : '';
  const suffix = key === '11-13-NEET' ? ' NEET' : key === '11-13-JEE' ? ' JEE' : '';
  return `Grades ${range}${suffix}`;
}

function averageOf(sum: number, count: number): number {
  return count > 0 ? sum / count : Number.NaN;
}

function subjectAverages(entry: GradeGroupStats): Map<string, number> {
  const out = new Map<string, number>();
  for (const [subject, sum] of entry.subjectPercentSums) {
    const count = entry.subjectPercentCounts.get(subject) ?? 0;
    out.set(subject, averageOf(sum, count));
  }
  return out;
}

function pickSubjectExtreme(
  averages: Map<string, number>,
  direction: 'best' | 'worst'
): { subject: string; percent: number } | undefined {
  let result: { subject: string; percent: number } | undefined;
  for (const [subject, percent] of averages) {
    if (Number.isNaN(percent)) continue;
    if (
      !result ||
      (direction === 'best' ? percent > result.percent : percent < result.percent)
    ) {
      result = { subject, percent };
    }
  }
  return result;
}

/**
 * Selects the best- or worst-performing grade group by average student
 * percentage, breaking ties by comparing each candidate group's most
 * extreme subject average (higher wins for "best", lower wins for "worst").
 * If still tied, keeps the first group in GRADE_GROUP_ORDER.
 */
function pickExtremeGroup(
  stats: Map<GradeGroupKey, GradeGroupStats>,
  direction: 'best' | 'worst'
): GradeGroupSubjectSummary | undefined {
  const candidates = GRADE_GROUP_ORDER.map((key) => stats.get(key))
    .filter((entry): entry is GradeGroupStats => !!entry && entry.studentCount > 0)
    .map((entry) => ({
      entry,
      avgPercent: averageOf(entry.studentPercentSum, entry.studentCount),
    }))
    .filter((c) => !Number.isNaN(c.avgPercent));

  if (candidates.length === 0) return undefined;

  const best = direction === 'best' ? Math.max : Math.min;
  const extremeValue = best(...candidates.map((c) => c.avgPercent));
  let tied = candidates.filter((c) => c.avgPercent === extremeValue);

  if (tied.length > 1) {
    const withTopSubject = tied.map((c) => ({
      ...c,
      subjectExtreme: pickSubjectExtreme(subjectAverages(c.entry), direction),
    }));
    const subjectValues = withTopSubject
      .map((c) => c.subjectExtreme?.percent)
      .filter((v): v is number => v != null);
    if (subjectValues.length > 0) {
      const subjectExtremeValue = best(...subjectValues);
      const rebroken = withTopSubject.filter(
        (c) => c.subjectExtreme?.percent === subjectExtremeValue
      );
      if (rebroken.length > 0) tied = rebroken;
    }
  }

  const chosen = tied[0];
  const subjectExtreme = pickSubjectExtreme(subjectAverages(chosen.entry), direction);

  return {
    key: chosen.entry.key,
    label: gradeGroupLabel(chosen.entry.key, chosen.entry.grades),
    avgPercent: chosen.avgPercent,
    topSubject: subjectExtreme?.subject,
    topSubjectPercent: subjectExtreme?.percent,
  };
}

function formatWholePercent(value: number): string {
  return `${Math.round(value)}%`;
}

function buildBestGradePerformanceSentence(
  summary: GradeGroupSubjectSummary | undefined
): string | undefined {
  if (!summary) return undefined;
  const percent = formatWholePercent(summary.avgPercent);
  if (summary.topSubject && summary.topSubjectPercent != null) {
    return `Overall, ${summary.label} have performed the best across subjects, scoring particularly high in ${summary.topSubject} with an average score of ${formatWholePercent(summary.topSubjectPercent)}.`;
  }
  return `Overall, ${summary.label} have performed the best across subjects with an average score of ${percent}.`;
}

function buildImprovementRecommendationSentence(
  summary: GradeGroupSubjectSummary | undefined
): string | undefined {
  if (!summary) return undefined;
  const percent = formatWholePercent(summary.avgPercent);
  if (summary.topSubject && summary.topSubjectPercent != null) {
    return `${summary.label} demonstrated the lowest performance with ${percent}, particularly in ${summary.topSubject} with just ${formatWholePercent(summary.topSubjectPercent)}, requiring targeted interventions.`;
  }
  return `${summary.label} demonstrated the lowest performance with ${percent}, requiring targeted interventions.`;
}

function buildSummary(
  bundle: SchoolReportWidgetBundles,
  gradewiseData: SchoolReportGradeBlock[]
): SchoolReportSummaryItem[] {
  const participation = buildParticipation(bundle.gradewiseRows);
  const [attempted, registered] = (participation.total_participation ?? '0/0').split('/');
  const gradeGroupStats = computeGradeGroupStats(bundle);
  const bestGroup = pickExtremeGroup(gradeGroupStats, 'best');
  const worstGroup = pickExtremeGroup(gradeGroupStats, 'worst');
  const { highest, lowest } = findAccuracyExtremes(collectSubjectAccuracies(gradewiseData));

  return [
    {
      participation_summary: `Your school achieved an outstanding ${Number(
        participation.overall_participation_percent ?? 0
      ).toFixed(
        1
      )}% rate, with ${attempted} participating out of ${registered} students. This reflects strong student engagement and effective outreach efforts!`,
      best_grade_performance: buildBestGradePerformanceSentence(bestGroup),
      subject_performance:
        highest && lowest
          ? `${describeSubjectEntry(highest)} had the highest subject performance with an accuracy of ${highest.accuracy}%, while ${describeSubjectEntry(
              lowest
            )} had the lowest with ${lowest.accuracy}%. Accuracy defines the correctness and precision of students answers, ensuring that they avoid careless mistakes and that each response contributes positively to their overall score.`
          : undefined,
      improvement_recommendation: buildImprovementRecommendationSentence(worstGroup),
    },
  ];
}

function buildMetadata(
  schoolId: string,
  bundle: SchoolReportWidgetBundles
): SchoolReportMetadata {
  const sample =
    bundle.gradewiseRows[0] ?? bundle.sectionwiseRows[0] ?? bundle.studentwiseRows[0] ?? {};
  return {
    school_name: asString(sample.SCHOOL_NAME ?? sample.SCHOOL, schoolId),
    event_name: asString(sample.EVENT_NAME, 'School'),
    level: asOptionalString(sample.LEVEL),
    round: asOptionalString(sample.ROUND),
    event_mode: asOptionalString(sample.EVENT_MODE),
    event_scheduled_date_time: asOptionalString(
      sample.SUBMITTED_DATE ?? sample.MAX_SUBMITTED_AT ?? sample.EXAM_DATE
    ),
  };
}

export function mapWidgetsToSchoolReportData(
  schoolId: string,
  bundle: SchoolReportWidgetBundles
): SchoolReportData {
  const gradewiseData = buildGradewiseData(bundle);
  return {
    metadata: buildMetadata(schoolId, bundle),
    summary: buildSummary(bundle, gradewiseData),
    participation_details: [buildParticipation(bundle.gradewiseRows)],
    gradewise_data: gradewiseData,
  };
}

function asRowArray(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (row): row is Record<string, unknown> => !!row && typeof row === 'object' && !Array.isArray(row)
  );
}

/** Unwrap analytics /widgets payload into query_output rows. */
export function extractQueryOutput(raw: unknown, widgetId: string): Record<string, unknown>[] {
  if (raw == null) return [];

  if (Array.isArray(raw)) {
    if (raw.length === 0) return [];
    const first = raw[0];
    if (
      first &&
      typeof first === 'object' &&
      !('widget_id' in first) &&
      !('query_output' in first)
    ) {
      return asRowArray(raw);
    }
    const results = raw as AnalyticsWidgetResult[];
    const match = results.find((item) => item.widget_id === widgetId) ?? results[0];
    return asRowArray(match?.query_output);
  }

  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.query_output)) return asRowArray(obj.query_output);
    if (Array.isArray(obj.data)) return extractQueryOutput(obj.data, widgetId);
    if (Array.isArray(obj.result)) return extractQueryOutput(obj.result, widgetId);
    if (Array.isArray(obj.results)) return extractQueryOutput(obj.results, widgetId);
  }

  return [];
}
